/**
 * /ai/documents/:documentId — the Document Review Workspace (the heart).
 *
 * Split view: PDF left (signed URL → react-pdf), right tabs:
 *   Fields     — template-section-grouped editable fields with provenance
 *                chips (grounded / verifier / LOS / human) + edit highlighting
 *   Validation — deterministic checks; Verifier — second-model agreement;
 *   LOS        — Arive mirror cross-check detail
 *   Markdown   — golden-format render; History — extraction versions +
 *                correction records
 *
 * Actions: Save correction (ledger only), Promote to golden (server-side
 * preview modal shows exact diffs + validation before commit), Re-extract,
 * Re-classify, manual status override.
 *
 * Field editing works on DOTTED PATHS over the current extraction
 * (document.loan_number, pricing.base.note_rate, …). Pricing adjustment rows
 * are edited as JSON in the Fields tab's table editor.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  HStack,
  VStack,
  Text,
  Button,
  IconButton,
  Input,
  Textarea,
  Spinner,
  Center,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Code,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft, FiRefreshCw, FiZap, FiTag, FiSave, FiAward, FiRotateCw, FiMail } from 'react-icons/fi';
import { callDocai, useRunPoller } from '../../hooks/useDocAI';
import StatusChip from '../../components/AI/StatusChip';
import ProvenanceBadge from '../../components/AI/ProvenanceBadge';
import ValidationChecklist from '../../components/AI/ValidationChecklist';
import DiffTable from '../../components/AI/DiffTable';
import RunProgressBar from '../../components/AI/RunProgressBar';
import PdfPane from '../../components/AI/PdfPane';

// ------------------------------------------------------------- path helpers

const getPath = (obj, dotPath) =>
  dotPath.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

/** Flatten an extraction into [{path, value}] rows, arrays as JSON leaves. */
function flattenExtraction(obj, prefix = '') {
  const rows = [];
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) {
    rows.push({ path: prefix, value: obj });
    return rows;
  }
  for (const [k, v] of Object.entries(obj)) {
    const p = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) rows.push(...flattenExtraction(v, p));
    else rows.push({ path: p, value: v });
  }
  return rows;
}

/** Group flat rows by their first path segment (template section targets). */
function groupBySection(rows) {
  const groups = new Map();
  for (const row of rows) {
    const seg = row.path.split('.')[0];
    if (!groups.has(seg)) groups.set(seg, []);
    groups.get(seg).push(row);
  }
  return [...groups.entries()];
}

const showValue = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v);
};

// ---------------------------------------------------------------- component

export default function AIDocumentWorkspacePage() {
  const { documentId } = useParams();
  const history = useHistory();
  const toast = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // { path: newValue }
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [activeRunId, setActiveRunId] = useState(null);
  const { run: activeRun } = useRunPoller(activeRunId);
  const promoteModal = useDisclosure();

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const editedBg = useColorModeValue('orange.50', 'orange.900');
  const sectionColor = useColorModeValue('gray.500', 'gray.400');
  const emailBoxBg = useColorModeValue('purple.50', 'rgba(128, 90, 213, 0.15)');
  const emailBoxBorder = useColorModeValue('purple.200', 'purple.600');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callDocai('docaiDocuments', { action: 'get', documentId });
      setData(res);
      setEdits({});
    } catch (err) {
      toast({ title: 'Load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [documentId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Reload when a triggered run completes.
  useEffect(() => {
    if (activeRun && ['done', 'error', 'cancelled'].includes(activeRun.status)) {
      if (activeRun.status === 'done') toast({ title: `${activeRun.kind} complete`, status: 'success', duration: 3000 });
      if (activeRun.status === 'error') toast({ title: `${activeRun.kind} failed`, description: activeRun.error, status: 'error', duration: 6000 });
      setActiveRunId(null);
      load();
    }
  }, [activeRun, load, toast]);

  const doc = data?.document;
  const extraction = data?.extraction?.extraction || data?.genericExtraction?.extraction || null;
  const validation = data?.extraction?.validation || null;
  const verifier = data?.extraction?.verifier || null;
  const xcheck = data?.extraction?.xcheck || null;

  // Per-path provenance: the verifier record carries a ready-made map of
  // { "document.loan_number": ["grounded", "verifier"], ... }.
  const provenance = useMemo(() => verifier?.provenance || {}, [verifier]);

  // LOS cross-check is an array of arive:* checks; matched = loan_lookup pass.
  const xcheckChecks = Array.isArray(xcheck) ? xcheck : null;
  const losMatched = xcheckChecks
    ? xcheckChecks.find((c) => c.id === 'arive:loan_lookup')?.level === 'pass'
    : null;

  const flatRows = useMemo(() => (extraction ? flattenExtraction(extraction) : []), [extraction]);
  const sections = useMemo(() => groupBySection(flatRows), [flatRows]);
  const editCount = Object.keys(edits).length;

  const editsArray = () =>
    Object.entries(edits).map(([path, value]) => {
      const original = getPath(extraction, path);
      if (typeof original === 'object' && original !== null) {
        // JSON leaf (e.g. pricing.adjustments) — parse the edited text.
        try {
          return { path, value: JSON.parse(value) };
        } catch (err) {
          throw new Error(`"${path}" is not valid JSON: ${err.message}`);
        }
      }
      return { path, value };
    });

  const openPromote = async () => {
    setPreviewLoading(true);
    try {
      const res = await callDocai('docaiReview', {
        action: 'preview',
        documentId,
        edits: editsArray(),
      });
      setPreview(res);
      promoteModal.onOpen();
    } catch (err) {
      toast({ title: 'Preview failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmPromote = async (force = false) => {
    setPromoting(true);
    try {
      const res = await callDocai('docaiReview', {
        action: 'promote',
        documentId,
        edits: editsArray(),
        notes: notes || null,
        force,
      });
      if (res.ok === false) {
        toast({
          title: `Promote blocked (${res.reason})`,
          description: res.detail || (res.errors || res.checks || res.lines || []).map((x) => x.detail || x.line || JSON.stringify(x)).slice(0, 3).join('; '),
          status: 'warning',
          duration: 8000,
        });
        return;
      }
      toast({ title: 'Promoted to golden', status: 'success', duration: 4000 });
      promoteModal.onClose();
      setNotes('');
      load();
    } catch (err) {
      toast({ title: 'Promote failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setPromoting(false);
    }
  };

  const saveCorrection = async () => {
    try {
      await callDocai('docaiReview', { action: 'saveCorrection', documentId, edits: editsArray(), notes: notes || null });
      toast({ title: 'Correction saved to ledger', status: 'success', duration: 3000 });
      setNotes('');
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  const trigger = async (payload, label) => {
    try {
      const res = await callDocai('docaiDocuments', payload);
      setActiveRunId(res.runId);
      toast({ title: `${label} queued`, status: 'info', duration: 2500 });
    } catch (err) {
      toast({ title: `${label} failed`, description: err.message, status: 'error', duration: 6000 });
    }
  };

  const setStatus = async (status) => {
    try {
      await callDocai('docaiReview', { action: 'setStatus', documentId, status });
      toast({ title: `Status → ${status}`, status: 'success', duration: 2500 });
      load();
    } catch (err) {
      toast({ title: 'Status change failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  if (loading && !data) {
    return (
      <Center h="70vh">
        <Spinner size="xl" color="blue.400" />
      </Center>
    );
  }
  if (!doc) {
    return (
      <Center h="70vh" flexDirection="column">
        <Text mb={3}>Document not found.</Text>
        <Button leftIcon={<FiArrowLeft />} onClick={() => history.goBack()}>Back</Button>
      </Center>
    );
  }

  return (
    <Flex direction="column" h="100%" minH={0}>
      {/* Header bar */}
      <HStack px={4} py={3} borderBottomWidth="1px" borderColor={cardBorder} spacing={3} flexWrap="wrap">
        <IconButton aria-label="Back" icon={<FiArrowLeft />} size="sm" variant="ghost" onClick={() => history.goBack()} />
        <Box minW={0} flex={1}>
          <Text fontSize="sm" fontFamily="mono" noOfLines={1}>{doc.file_name}</Text>
          <HStack spacing={2} mt={1}>
            <StatusChip status={doc.status} />
            {doc.doc_type && <Tag size="sm">{doc.doc_type}{doc.lender ? ` · ${doc.lender}` : ''}</Tag>}
            {doc.loan_number && <Tag size="sm" fontFamily="mono">#{doc.loan_number}</Tag>}
            {doc.skill_name && <Tag size="sm" colorScheme="blue">{doc.skill_name}</Tag>}
            {data.golden && <Tag size="sm" colorScheme="purple">golden</Tag>}
          </HStack>
        </Box>
        <HStack spacing={2}>
          <Select size="sm" w="160px" value="" placeholder="Set status…" onChange={(e) => e.target.value && setStatus(e.target.value)}>
            {['verified', 'review', 'auto_accept', 'accept_with_warnings', 'queued'].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Select>
          <Button size="sm" leftIcon={<FiZap />} variant="outline" onClick={() => trigger({ action: 'reprocess', documentId, force: true }, 'Re-extract')}>
            Re-extract
          </Button>
          <Button size="sm" leftIcon={<FiTag />} variant="outline" onClick={() => trigger({ action: 'reclassify', documentId }, 'Re-classify')}>
            Re-classify
          </Button>
          <Button size="sm" leftIcon={<FiRefreshCw />} variant="ghost" onClick={load} isLoading={loading} />
        </HStack>
      </HStack>

      {activeRun && (
        <Box px={4} py={2} borderBottomWidth="1px" borderColor={cardBorder}>
          <RunProgressBar run={activeRun} />
        </Box>
      )}

      {/* Email intake provenance */}
      {doc.source === 'email' && doc.source_meta && (() => {
        const sm = doc.source_meta;
        // Lender templates differ in shape; doc.loan_number is backfilled from
        // each skill's matching.loanNumberPath after extraction.
        const extractedLoan = getPath(extraction || {}, 'document.loan_number') || doc.loan_number || null;
        // Suffix-aware: Plaza masks to the last 4 (XXXXXX0939) in both the
        // subject and the document, so compare digit suffixes.
        const subjDigits = String(sm.subjectLoanNumber || '').replace(/\D/g, '');
        const extrDigits = String(extractedLoan || '').replace(/\D/g, '');
        const loanMatch = subjDigits.length >= 4 && extrDigits.length >= 4
          ? (subjDigits === extrDigits || subjDigits.endsWith(extrDigits) || extrDigits.endsWith(subjDigits))
          : null;
        return (
          <Box px={4} py={2} bg={emailBoxBg} borderBottomWidth="1px" borderColor={emailBoxBorder}>
            <HStack spacing={3} flexWrap="wrap" fontSize="xs">
              <Tag size="sm" colorScheme="purple" flexShrink={0}>
                <Box as={FiMail} mr={1} /> Auto-detected from email
              </Tag>
              <Text color="gray.500">
                {sm.inbox}{sm.receivedAt ? ` · ${new Date(sm.receivedAt).toLocaleString()}` : ''}
                {sm.changeType ? ` · ${sm.changeType}` : ''}
              </Text>
              <Text noOfLines={1} flex={1} minW="200px" title={sm.subject}>{sm.subject}</Text>
              {loanMatch === true && (
                <Tag size="sm" colorScheme="green" flexShrink={0}>✓ loan # matches subject ({sm.subjectLoanNumber})</Tag>
              )}
              {loanMatch === false && (
                <Tag size="sm" colorScheme="orange" flexShrink={0}>
                  ⚠ subject {sm.subjectLoanNumber} ≠ extracted {extractedLoan}
                </Tag>
              )}
              {loanMatch === null && sm.subjectLoanNumber && (
                <Tag size="sm" variant="subtle" flexShrink={0}>subject loan {sm.subjectLoanNumber} · awaiting extraction</Tag>
              )}
            </HStack>
          </Box>
        );
      })()}

      {/* Split view */}
      <Flex flex={1} minH={0}>
        {/* PDF pane */}
        <Box w="46%" minW="380px" borderRightWidth="1px" borderColor={cardBorder} display="flex" flexDirection="column">
          <PdfPane url={data.pdfUrl} fileName={doc.file_name} />
        </Box>

        {/* Right tabs */}
        <Box flex={1} minW={0} overflowY="auto">
          <Tabs size="sm" px={2} pt={2} colorScheme="blue" isLazy>
            <TabList flexWrap="wrap">
              <Tab>Fields{editCount ? ` (${editCount} edited)` : ''}</Tab>
              <Tab>Validation{validation?.failCount ? ` (${validation.failCount})` : ''}</Tab>
              <Tab>Verifier</Tab>
              <Tab>LOS Match</Tab>
              <Tab>Markdown</Tab>
              <Tab>History</Tab>
            </TabList>
            <TabPanels>
              {/* ------------------------------------------------ Fields */}
              <TabPanel px={2}>
                {!extraction && (
                  <Text fontSize="sm" color="gray.500">
                    No extraction yet — run Re-extract, or Re-classify if the document is unrouted.
                  </Text>
                )}
                {sections.map(([section, rows]) => (
                  <Box key={section} mb={5}>
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color={sectionColor} mb={2}>
                      {section.replace(/_/g, ' ')}
                    </Text>
                    <VStack align="stretch" spacing={1}>
                      {rows.map(({ path, value }) => {
                        const edited = path in edits;
                        const isJsonLeaf = value !== null && typeof value === 'object';
                        const displayed = edited ? edits[path] : showValue(value);
                        return (
                          <HStack
                            key={path}
                            align="flex-start"
                            spacing={2}
                            bg={edited ? editedBg : 'transparent'}
                            borderRadius="md"
                            px={2}
                            py={1}
                          >
                            <Box flex="0 0 44%" minW={0}>
                              <Text fontSize="xs" fontFamily="mono" color={sectionColor} noOfLines={2}>
                                {path.split('.').slice(1).join('.') || path}
                              </Text>
                              <HStack spacing={1} mt={0.5}>
                                {[...new Set(provenance[path] || [])].map((k) => (
                                  <ProvenanceBadge key={k} kind={k} />
                                ))}
                                {edited && <ProvenanceBadge kind="edited" />}
                              </HStack>
                            </Box>
                            {isJsonLeaf ? (
                              <Textarea
                                size="xs"
                                fontFamily="mono"
                                rows={Math.min(10, (displayed.match(/\n/g) || []).length + 2)}
                                value={displayed}
                                onChange={(e) => setEdits((prev) => ({ ...prev, [path]: e.target.value }))}
                              />
                            ) : (
                              <Input
                                size="xs"
                                fontFamily="mono"
                                value={displayed}
                                placeholder="∅"
                                onChange={(e) => setEdits((prev) => ({ ...prev, [path]: e.target.value }))}
                              />
                            )}
                          </HStack>
                        );
                      })}
                    </VStack>
                  </Box>
                ))}
                {extraction && (
                  <Box position="sticky" bottom={0} bg={cardBg} borderTopWidth="1px" borderColor={cardBorder} py={3} px={2}>
                    <Input
                      size="sm"
                      placeholder="Notes for the correction ledger (optional)"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      mb={2}
                    />
                    <HStack>
                      <Button
                        size="sm"
                        leftIcon={<FiSave />}
                        variant="outline"
                        isDisabled={!editCount}
                        onClick={saveCorrection}
                      >
                        Save correction
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<FiAward />}
                        colorScheme="purple"
                        isLoading={previewLoading}
                        onClick={openPromote}
                      >
                        Promote to golden…
                      </Button>
                      {editCount > 0 && (
                        <Button size="sm" variant="ghost" leftIcon={<FiRotateCw />} onClick={() => setEdits({})}>
                          Discard edits
                        </Button>
                      )}
                    </HStack>
                  </Box>
                )}
              </TabPanel>

              {/* -------------------------------------------- Validation */}
              <TabPanel px={2}>
                {validation ? (
                  <>
                    <HStack mb={3} spacing={3}>
                      <StatusChip status={validation.status} />
                      <Text fontSize="xs" color="gray.500">
                        {validation.failCount} fail · {validation.warnCount} warn
                      </Text>
                    </HStack>
                    <ValidationChecklist checks={validation.checks || []} />
                  </>
                ) : (
                  <Text fontSize="sm" color="gray.500">No validation recorded.</Text>
                )}
              </TabPanel>

              {/* ---------------------------------------------- Verifier */}
              <TabPanel px={2}>
                {verifier ? (
                  <>
                    <HStack mb={3} spacing={3} flexWrap="wrap">
                      <Tag size="sm" colorScheme={verifier.comparison?.verified || verifier.verified ? 'green' : 'orange'}>
                        {(verifier.comparison?.verified ?? verifier.verified) ? 'verified' : 'disagreements'}
                      </Tag>
                      <Text fontSize="xs" color="gray.500">model {verifier.meta?.model || verifier.model}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {(verifier.comparison?.targetedPaths || verifier.targetedPaths || []).length} targeted fields
                      </Text>
                    </HStack>
                    <DiffTable
                      rows={(verifier.comparison?.disagreements || verifier.disagreements || []).map((d) => ({
                        path: d.path,
                        model: d.primary ?? d.actual,
                        corrected: d.verifier ?? d.expected,
                      }))}
                      leftLabel="Primary"
                      rightLabel="Verifier"
                    />
                  </>
                ) : (
                  <Text fontSize="sm" color="gray.500">Verifier has not run for this extraction.</Text>
                )}
              </TabPanel>

              {/* --------------------------------------------------- LOS */}
              <TabPanel px={2}>
                {xcheckChecks ? (
                  <>
                    <HStack mb={3}>
                      <Tag size="sm" colorScheme={losMatched ? 'green' : 'red'}>
                        {losMatched ? 'LOS matched' : 'no LOS match'}
                      </Tag>
                      {doc.arive_loan_id && (
                        <Text fontSize="xs" fontFamily="mono">arive_loan_id {doc.arive_loan_id}</Text>
                      )}
                    </HStack>
                    <ValidationChecklist checks={xcheckChecks} showPasses />
                  </>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    LOS cross-check has not run for this extraction (reprocess runs it automatically).
                  </Text>
                )}
              </TabPanel>

              {/* ---------------------------------------------- Markdown */}
              <TabPanel px={2}>
                <Code
                  as="pre"
                  display="block"
                  w="100%"
                  p={3}
                  fontSize="xs"
                  whiteSpace="pre-wrap"
                  borderRadius="md"
                >
                  {data.extraction?.markdown || data.golden?.markdown || 'No markdown rendered.'}
                </Code>
              </TabPanel>

              {/* ----------------------------------------------- History */}
              <TabPanel px={2}>
                <Heading size="xs" mb={2}>Extractions</Heading>
                <Table size="sm" mb={5}>
                  <Thead><Tr><Th>Model</Th><Th>Prompt</Th><Th>Current</Th><Th>When</Th></Tr></Thead>
                  <Tbody>
                    {(data.extractions || []).map((e) => (
                      <Tr key={e.id}>
                        <Td fontSize="xs">{e.model}{e.skill_name === '_generic' ? ' (generic)' : ''}</Td>
                        <Td fontSize="xs" fontFamily="mono">{e.prompt_hash}</Td>
                        <Td fontSize="xs">{e.is_current ? '✓' : ''}</Td>
                        <Td fontSize="xs">{new Date(e.created_at).toLocaleString()}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                <Heading size="xs" mb={2}>Corrections</Heading>
                {(data.corrections || []).map((c) => (
                  <Box key={c.id} borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={3} mb={3}>
                    <HStack justify="space-between" mb={2}>
                      <Tag size="sm" colorScheme={c.status === 'open' ? 'orange' : 'green'}>{c.status}</Tag>
                      <Text fontSize="xs" color="gray.500">{new Date(c.created_at).toLocaleString()}</Text>
                    </HStack>
                    <DiffTable rows={c.field_diffs || []} />
                  </Box>
                ))}
                {!(data.corrections || []).length && (
                  <Text fontSize="sm" color="gray.500">No corrections recorded for this document.</Text>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Flex>

      {/* Promote confirm modal */}
      <Modal isOpen={promoteModal.isOpen} onClose={promoteModal.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Promote to golden</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {preview && (
              <>
                <HStack mb={3} spacing={3}>
                  <StatusChip status={preview.validation?.status} />
                  <Text fontSize="xs" color="gray.500">
                    {preview.validation?.failCount || 0} fail · {preview.validation?.warnCount || 0} warn
                  </Text>
                </HStack>
                {(preview.validation?.notPass || []).length > 0 && (
                  <Box mb={4}>
                    <ValidationChecklist checks={preview.validation.notPass} />
                  </Box>
                )}
                <Heading size="xs" mb={2}>
                  Field changes vs the model extraction ({(preview.fieldDiffs || []).length})
                </Heading>
                <DiffTable rows={preview.fieldDiffs || []} />
                <Text fontSize="xs" color="gray.500" mt={4}>
                  Promoting stores this exact content as the golden for the document, records the diffs in the
                  correction ledger, and marks the document verified — identical semantics to CLI `docai promote`.
                </Text>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={promoteModal.onClose}>Cancel</Button>
            {preview?.validation?.failCount > 0 && (
              <Button colorScheme="orange" mr={3} isLoading={promoting} onClick={() => confirmPromote(true)}>
                Force promote (failing checks)
              </Button>
            )}
            <Button
              colorScheme="purple"
              isLoading={promoting}
              isDisabled={preview?.validation?.failCount > 0}
              onClick={() => confirmPromote(false)}
            >
              Promote
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
